import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { DataPoolName } from 'dashboards/types/data_pool';
import { FilterInput } from 'dashboards/types/filter';
import { ClickhouseCursorParams, PageInfo } from 'dashboards/types/pagination';
import { Sort } from 'types/column';
import { TimeRangeInput } from 'dashboards/types/time';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

type UseDataGridParams = {
  dataPool: { name: DataPoolName };
  timeRange: TimeRangeInput;
  columns: Array<string>;
  sort: Sort;
  filters?: Array<FilterInput>;
  orderByColumn: string;
  timeZone: string;
};

type UseDataGridOptions = UseQueryOptions<
  { headers: string[]; rows: string[][]; pageInfo: PageInfo },
  APIError
> & {
  params: UseDataGridParams & ClickhouseCursorParams;
};

export function useDataGrid({
  queryKey = ['data-grid'],
  params: { orderByColumn = 'timestamp', ...params },
  ...options
}: UseDataGridOptions) {
  const { applicationID, client } = useAPI();

  const columnIndex = params.columns.indexOf(orderByColumn);
  const orderByColumnIndex =
    orderByColumn === 'timestamp' || columnIndex < 0 ? 1 : columnIndex + 1; // Query expects 1-based index

  const queryParams: Omit<UseDataGridParams, 'orderByColumn' | 'dataPool'> & {
    table: string;
    orderByColumn: number;
  } = {
    first: 50,
    table: params.dataPool.name,
    orderByColumn: orderByColumnIndex,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, queryParams],
    async () => {
      const { data } = await client.post(
        `/apps/${applicationID}/user_metrics/datagrid`,
        queryParams
      );

      return data;
    },
    { ...options }
  );
}
